/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { useBrowserSize } from '../../../../contexts/BrowserSize';
import {
  LG_SCREEN_MIN,
  MD_SCREEN_MIN,
  SM_SCREEN_MAX,
} from '../../../../modules/Styleguide';
import { CDN_PATH } from '../constants';
import {
  PageHeader,
  ScrollToContainer,
  Section,
  SectionContent,
  SectionHeader,
  ValuePropLabel,
} from '../styled';
import { HostingIcon } from './HostingAndDistributionSvgs';

export function HostingAndDistributionSection() {
  const { width } = useBrowserSize();
  const isMobileBreakpoint = width <= MD_SCREEN_MIN;

  function HostingHeader() {
    return (
      <div>
        <ValuePropLabel>Hosting and Distribution</ValuePropLabel>
        <PageHeader>
          Host unlimited episodes for free and get heard everywhere with a
          single click.
        </PageHeader>
      </div>
    );
  }

  function FreeCallout() {
    return (
      <Callout>
        <strong>Anchor is completely free—</strong> no storage limits, no trial
        period, no strings attached.
        <a
          href="https://blog.anchor.fm/why-anchor-is-free"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read why &rarr;
        </a>
      </Callout>
    );
  }

  return (
    <ScrollToContainer id="hosting">
      <Section
        id="hosting"
        css={css`
          background: #8940fa;
          @media (min-width: ${MD_SCREEN_MIN}px) {
            background: linear-gradient(
              90deg,
              #8940fa 0%,
              #8940fa 60%,
              #23213d 60%,
              #23213d 100%
            );
          }
        `}
      >
        <MobileContainer>
          <SectionContent
            css={css`
              padding-top: 100px;
              padding-bottom: 100px;
            `}
          >
            <HostingHeader />
          </SectionContent>
          <div
            css={css`
              position: relative;
            `}
          >
            <picture>
              <img
                // @ts-ignore;
                loading="lazy"
                width="100%"
                alt="cover art creator screen in Anchor app"
                src={`${CDN_PATH}/pull-quote-img-mobile.jpg`}
              />
            </picture>
            <FreeCallout />
          </div>
        </MobileContainer>

        <SectionContent
          css={css`
            align-items: flex-start;
            @media (min-width: ${MD_SCREEN_MIN}px) {
              padding-right: 0 !important;
            }
          `}
        >
          <div
            css={css`
              @media (min-width: ${MD_SCREEN_MIN}px) {
                width: calc(60% - 65px);
              }
              @media (min-width: ${LG_SCREEN_MIN}px) {
                width: calc(60% - 70px);
              }
            `}
          >
            <StyledSectionContent>
              {!isMobileBreakpoint && <HostingHeader />}
              <div
                css={css`
                  margin-top: 70px;
                  margin-bottom: 20px;
                `}
              >
                <HostingIcon />
              </div>
              <SectionHeader>100% Free Hosting</SectionHeader>
              <p>
                We believe podcasting is a powerful medium that ought to be
                accessible to as many people as possible. That’s why Anchor is
                completely free—no storage limits, no trial period, no strings
                attached.
              </p>
              <p>
                You own all of your content, you don’t need to monetize with
                Anchor in order to host your episodes, and we won’t ask for your
                payment info at any time. Because with today’s technology,{' '}
                <a
                  href="https://medium.com/@NirZicherman/why-you-should-never-pay-for-podcast-hosting-9c39becd7cf7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  you shouldn’t have to spend money to share your voice.
                </a>
              </p>
              <img
                css={css`
                  margin-top: 70px;
                  margin-bottom: 20px;
                  @media (min-width: ${MD_SCREEN_MIN}px) {
                    max-width: 300px;
                  }
                `}
                // @ts-ignore;
                loading="lazy"
                width="100%"
                alt="logos of distribution platforms available on Anchor"
                src={`${CDN_PATH}/listening-platforms.svg`}
              />
              <SectionHeader>One-Click Distribution</SectionHeader>
              <p>
                Distribute your podcast to all the platforms you care about,
                including Spotify, Apple Podcasts, Google Podcasts, and{' '}
                <a
                  href="http://anch.co/distribution"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  more
                </a>
                .
              </p>
              <p>
                It only takes a click to get heard everywhere. And if you prefer
                to manage your RSS feed and distribute your podcast yourself, we
                make it easy to do that too.
              </p>
            </StyledSectionContent>
          </div>
          <ScrollingStickyContainer>
            <img
              // @ts-ignore;
              loading="lazy"
              alt="anchor application on multiple devices"
              src={`${CDN_PATH}/pull-quote-img-desk.jpg`}
            />
            <FreeCallout />
          </ScrollingStickyContainer>
        </SectionContent>
      </Section>
    </ScrollToContainer>
  );
}

const Callout = styled.span`
  color: white;
  position: absolute;
  text-align: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  font-size: 2.4rem;
  line-height: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  padding: 0 20px;
  strong,
  a {
    display: block;
  }
  a,
  a:active,
  a:focus,
  a:active:focus {
    color: #2effd9;
    color: #2effd9;
    font-size: 1.6rem;
    margin-top: 10px;
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    font-size: 3rem;
    line-height: 3.6rem;
    height: 90%;
    padding: 0 48px;
  }
`;

const MobileContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #8940fa;
  h1,
  h2,
  h4,
  p {
    color: white;
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    visibility: hidden;
    display: none;
  }
`;

const ScrollingStickyContainer = styled.div`
  position: sticky;
  top: 154px;
  width: 50%;
  height: 500px;
  background-image: none;
  background-size: cover;
  overflow: visible;
  @media (max-width: ${SM_SCREEN_MAX}px) {
    visibility: hidden;
    display: none;
  }
  img {
    display: none;
    visibility: hidden;
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    height: calc(110vh - 127px);
    width: 60%;
    top: 127px;
    background-image: url(${CDN_PATH}/pull-quote-img-tablet.jpg);
  }
  @media (min-width: ${LG_SCREEN_MIN}px) {
    height: calc(100vh - 146px);
    width: 50%;
    top: 146px;
    background-image: url(${CDN_PATH}/pull-quote-img-desktop.jpg);
  }
`;

const StyledSectionContent = styled.div`
  flex-direction: column;
  padding-top: 0;
  padding-bottom: 100px;
  padding-left: 0;
  padding-right: 40px;
  h1,
  h2,
  h4,
  p {
    color: white;
  }
  p {
    margin: 10px 0;
  }
  a,
  a:active,
  a:focus,
  a:active:focus {
    color: #2effd9;
  }
  @media (min-width: ${MD_SCREEN_MIN}px) {
    padding-top: 100px;
    padding-left: 0;
    p {
      max-width: 300px;
    }
  }
  @media (min-width: ${LG_SCREEN_MIN}px) {
    padding-left: 0;
  }
`;
